import { useEffect, useState } from "react";
import Box from "elements/Box";
import {
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Switch,
  TextField,
} from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { useSnackbar } from "notistack";
import DateRange from "elements/DateRange";
import moment from "moment";
import { ApiRequest } from "GlobalFunctions";

const MODULE_OPTIONS = [
  { key: "orders", label: "Pedidos" },
  { key: "purchases_orders", label: "Pedidos - Compra" },
  { key: "inactive_users", label: "Usuarios inactivos" },
  { key: "guides", label: "Guías" },
  { key: "inventory_counts", label: "Historial de conteos de inventario" },
  { key: "inventory_movements", label: "Historial de movimientos" },
  { key: "payments", label: "Recaudos" },
];

export default function Maintenance(props: any) {
  const { setLoading } = props;
  const { enqueueSnackbar } = useSnackbar();

  const [date, setDate] = useState<any>(null);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Company selection states
  const [allCompanies, setAllCompanies] = useState(true);
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<any[]>([]);

  useEffect(() => {
    setLoading(false);
    getCompanies();
  }, []);

  const getCompanies = () => {
    ApiRequest({
      url: "/companies/list",
      method: "get",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      query: { full_list: true },
      setResponse: (response: any) => {
        if (response.list) {
          setCompanies(response.list);
        }
      },
    });
  };

  const handleModuleToggle = (key: string) => {
    setSelectedModules((prev) =>
      prev.includes(key) ? prev.filter((m) => m !== key) : [...prev, key]
    );
  };

  const selectedLabels = MODULE_OPTIONS.filter((m) =>
    selectedModules.includes(m.key)
  ).map((m) => m.label);

  const canSubmit =
    date?.startDate &&
    date?.endDate &&
    selectedModules.length > 0 &&
    (allCompanies || selectedCompanies.length > 0);

  const handleOpenConfirm = () => {
    setConfirmText("");
    setConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    setConfirmOpen(false);
    setConfirmText("");
  };

  const handleSubmit = async () => {
    if (!confirmText) {
      enqueueSnackbar("Debes ingresar la contraseña maestra", { variant: "error" });
      return;
    }

    setSubmitting(true);
    setLoading(true);
    setConfirmOpen(false);

    await ApiRequest({
      url: "/maintenance/purge",
      method: "post",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      body: {
        startDate: date.startDate,
        endDate: date.endDate,
        modules: selectedModules,
        allCompanies,
        companyIds: allCompanies ? [] : selectedCompanies.map((c: any) => c.company_id),
        masterPassword: confirmText,
      },
      setResponse: (response: any) => {
        if (response.status === "success") {
          enqueueSnackbar(response.message, { variant: "success" });
          setDate(null);
          setSelectedModules([]);
          setAllCompanies(true);
          setSelectedCompanies([]);
        } else if (response.status === "partial") {
          enqueueSnackbar(response.message, { variant: "warning" });
        } else {
          enqueueSnackbar(response.message || "Error al eliminar datos", { variant: "error" });
        }
        setSubmitting(false);
        setConfirmText("");
        setLoading(false);
      },
    });
  };

  return (
    <div id="Maintenance" className="ModuleContainer">
      <div className="ModuleWrapper">
        <div className="boxContainerHeader">
          <div className="boxContainerTitle">
            <h2 className="h2_title">Mantenimiento</h2>
            <span className="h2_subtitle">
              Elimina datos históricos por periodo de tiempo
            </span>
          </div>
        </div>

        <Box id="MaintenanceBox" className="box100">
          {/* DATE RANGE */}
          <DateRange
            className="filters_datePicker"
            label="Periodo de tiempo"
            variant="outlined"
            fullWidth
            value={{
              startDate: date?.startDate || null,
              endDate: date?.endDate || null,
            }}
            format="MMMM DD, yyyy"
            onChange={(e: any) => {
              if (e.startDate && e.endDate) {
                const startDate = moment(e.startDate).format("YYYY-MM-DD");
                const endDate = moment(e.endDate).format("YYYY-MM-DD");
                setDate({ startDate, endDate });
              }
            }}
            variantDatePicker="side-by-side"
          />

          {/* MODULE SELECTION */}
          {date?.startDate && date?.endDate && (
            <>
              {/* COMPANY SELECTION */}
              <h3 style={{ marginTop: 24, marginBottom: 8 }}>
                Empresas
              </h3>
              <FormControlLabel
                control={
                  <Switch
                    checked={allCompanies}
                    onChange={(e) => {
                      setAllCompanies(e.target.checked);
                      if (e.target.checked) setSelectedCompanies([]);
                    }}
                  />
                }
                label="Todas las empresas"
              />

              {!allCompanies && (
                <Autocomplete
                  multiple
                  options={companies}
                  getOptionLabel={(option: any) => option.company_name || ""}
                  value={selectedCompanies}
                  onChange={(_e, newValue) => setSelectedCompanies(newValue)}
                  isOptionEqualToValue={(option: any, value: any) =>
                    option.company_id === value.company_id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Seleccionar empresas"
                      variant="outlined"
                      placeholder="Buscar empresa..."
                    />
                  )}
                  sx={{ mt: 1 }}
                />
              )}

              <h3 style={{ marginTop: 24, marginBottom: 8 }}>
                Selecciona los datos a eliminar
              </h3>
              <FormGroup>
                {MODULE_OPTIONS.map((mod) => (
                  <FormControlLabel
                    key={mod.key}
                    control={
                      <Checkbox
                        checked={selectedModules.includes(mod.key)}
                        onChange={() => handleModuleToggle(mod.key)}
                      />
                    }
                    label={mod.label}
                  />
                ))}
              </FormGroup>

              <Button
                variant="contained"
                color="error"
                fullWidth
                startIcon={<DeleteForeverIcon />}
                disabled={!canSubmit || submitting}
                onClick={handleOpenConfirm}
                sx={{ mt: 3 }}
              >
                Eliminar datos
              </Button>
            </>
          )}
        </Box>
      </div>

      {/* CONFIRMATION DIALOG */}
      <Dialog open={confirmOpen} onClose={handleCloseConfirm}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Borrarás <strong>{selectedLabels.join(", ")}</strong> para el periodo{" "}
            <strong>{date?.startDate} - {date?.endDate}</strong>.
            {allCompanies
              ? " Aplica a TODAS las empresas."
              : ` Aplica a: ${selectedCompanies.map((c: any) => c.company_name).join(", ")}.`}
            {" "}Ingresa la <strong>contraseña maestra</strong> para continuar.
          </DialogContentText>
          <TextField
            autoFocus
            label="Contraseña maestra"
            type="password"
            variant="outlined"
            fullWidth
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Contraseña maestra"
            helperText="Ingresa la contraseña maestra para autorizar la eliminación"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!confirmText || submitting}
            onClick={handleSubmit}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
